import React, { useEffect, useCallback } from 'react';
import { Box, IconButton, Typography, Slide, useTheme, alpha } from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';

export interface ToastNotification {
  id: number;
  title: string;
  message: string;
  link?: string;
  type?: 'info' | 'warning' | 'success' | 'error';
  /** Internal key so duplicate notification ids can still animate in */
  toastKey: number;
}

interface NotificationToastStackProps {
  items: ToastNotification[];
  onDismiss: (toastKey: number) => void;
  onClick: (item: ToastNotification) => void;
  /** Which side of the screen to stack toasts (Telegram/WhatsApp style) */
  anchor?: 'left' | 'right';
}

const TYPE_CONFIG = {
  info: { color: '#2196f3', Icon: InfoIcon },
  warning: { color: '#ff9800', Icon: WarningIcon },
  success: { color: '#4caf50', Icon: SuccessIcon },
  error: { color: '#f44336', Icon: ErrorIcon },
} as const;

const AUTO_DISMISS_MS = 7000;

function ToastItem({
  item,
  onDismiss,
  onClick,
  anchor,
}: {
  item: ToastNotification;
  onDismiss: (key: number) => void;
  onClick: (item: ToastNotification) => void;
  anchor: 'left' | 'right';
}) {
  const theme = useTheme();
  const type = item.type && TYPE_CONFIG[item.type] ? item.type : 'info';
  const { color, Icon } = TYPE_CONFIG[type];

  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(item.toastKey), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [item.toastKey, onDismiss]);

  return (
    <Slide direction={anchor === 'right' ? 'left' : 'right'} in mountOnEnter unmountOnExit>
      <Box
        role="alert"
        onClick={() => onClick(item)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          p: 1.5,
          pr: 1,
          mb: 1.25,
          maxWidth: 380,
          width: '100%',
          borderRadius: 2,
          cursor: item.link ? 'pointer' : 'default',
          bgcolor: theme.palette.mode === 'dark' ? '#2b2b2b' : '#ffffff',
          color: theme.palette.text.primary,
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0,0,0,0.45)'
              : '0 8px 28px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
          border: `1px solid ${alpha(color, 0.25)}`,
          borderLeft: anchor === 'right' ? `4px solid ${color}` : undefined,
          borderRight: anchor === 'left' ? `4px solid ${color}` : undefined,
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': item.link
            ? {
                transform: 'translateY(-1px)',
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 12px 36px rgba(0,0,0,0.55)'
                    : '0 12px 32px rgba(0,0,0,0.18)',
              }
            : undefined,
        }}
      >
        <Box
          sx={{
            mt: 0.25,
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: alpha(color, 0.12),
            color,
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {item.title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.25,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.45,
            }}
          >
            {item.message}
          </Typography>
        </Box>

        <IconButton
          size="small"
          aria-label="Dismiss"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(item.toastKey);
          }}
          sx={{ mt: -0.5, mr: -0.5, color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Slide>
  );
}

const NotificationToastStack: React.FC<NotificationToastStackProps> = ({
  items,
  onDismiss,
  onClick,
  anchor = 'right',
}) => {
  const handleDismiss = useCallback(
    (toastKey: number) => {
      onDismiss(toastKey);
    },
    [onDismiss]
  );

  if (items.length === 0) return null;

  return (
    <Box
      aria-live="polite"
      sx={{
        position: 'fixed',
        top: { xs: 72, sm: 80 },
        ...(anchor === 'right' ? { right: { xs: 12, sm: 20 } } : { left: { xs: 12, sm: 20 } }),
        zIndex: (theme) => theme.zIndex.snackbar + 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: anchor === 'right' ? 'flex-end' : 'flex-start',
        pointerEvents: 'none',
        maxHeight: 'calc(100vh - 96px)',
        overflow: 'hidden',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      {items.length > 0 && items.length >= 5 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'action.hover',
            opacity: 0.9,
          }}
        >
          <NotificationsIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" color="text.secondary">
            {items.length}
          </Typography>
        </Box>
      )}
      {items.map((item) => (
        <ToastItem
          key={item.toastKey}
          item={item}
          onDismiss={handleDismiss}
          onClick={onClick}
          anchor={anchor}
        />
      ))}
    </Box>
  );
};

export default NotificationToastStack;
